import api from './client'
import { cacheAutoSound } from '../utils/autoSound'

interface LoginResponse {
  ok: boolean
  access_token: string
  refresh_token: string
  user: User
}

// 2FA (см. TZ_admin_2fa.md §4.1): /auth/login и /auth/telegram при
// применимой 2FA возвращают 200 с этой формой вместо токенов.
export interface RequiresTwoFactor {
  ok: false
  requires_2fa: true
  challenge_id: string
  method: 'email' | 'telegram' | 'totp'
  hint?: string | null
}

function isRequiresTwoFactor(data: unknown): data is RequiresTwoFactor {
  return !!data && typeof data === 'object' && (data as { requires_2fa?: boolean }).requires_2fa === true
}

export interface User {
  id: string
  email: string
  name?: string
  role: string
  credits_balance: number
  telegram_id?: number | null
  avatar_url?: string | null
  // Дефолт режима памяти ИИ для новых чат-сессий. Используется как
  // стартовое значение в чате до создания сессии. Обновляется автоматически
  // при PATCH /chat/sessions/{id} с новым context_mode (UX continuity).
  default_chat_context_mode?: string
  // «Автоматически включать звук» у аудио/видео. Настройка аккаунта;
  // плееры читают её синхронно из зеркала в localStorage (utils/autoSound).
  auto_sound?: boolean
  // Статус подписки от GET /user/me. Используется фронтом, чтобы скрыть
  // оформление подписки в попапе тарифов при уже активной подписке.
  subscription_status?: string
  subscription_expires_at?: string | null
}

function getUtmData(): Record<string, string> {
  try {
    const raw = localStorage.getItem('umnik_utm')
    if (raw) return JSON.parse(raw)
  } catch { /* битый UTM-json в localStorage — игнорируем */ }
  return {}
}

export async function login(email: string, password: string, captchaToken?: string): Promise<User | RequiresTwoFactor> {
  const body: Record<string, string> = { email, password }
  if (captchaToken) body.captcha_token = captchaToken
  const response = await api.request<LoginResponse | RequiresTwoFactor>('/api/v1/auth/login', {
    method: 'POST',
    body,
    skipAuth: true,
  })
  if (isRequiresTwoFactor(response)) return response
  api.setTokens(response.access_token, response.refresh_token)
  cacheAutoSound(response.user.auto_sound)
  return response.user
}

// ── 2FA: подтверждение кода (логин/step-up) ─────────────────────────

export async function verify2FA(challengeId: string, code: string): Promise<User> {
  const response = await api.request<LoginResponse>('/api/v1/auth/2fa/verify', {
    method: 'POST',
    body: { challenge_id: challengeId, code },
    skipAuth: true,
  })
  api.setTokens(response.access_token, response.refresh_token)
  cacheAutoSound(response.user.auto_sound)
  return response.user
}

export async function resend2FA(challengeId: string): Promise<void> {
  await api.request('/api/v1/auth/2fa/resend', {
    method: 'POST',
    body: { challenge_id: challengeId },
    skipAuth: true,
  })
}

export async function fallbackEmail2FA(challengeId: string): Promise<RequiresTwoFactor> {
  const response = await api.request<{ ok: boolean; challenge_id: string; method: string; hint?: string | null }>(
    '/api/v1/auth/2fa/fallback-email',
    { method: 'POST', body: { challenge_id: challengeId }, skipAuth: true },
  )
  return { ok: false, requires_2fa: true, challenge_id: response.challenge_id, method: response.method as RequiresTwoFactor['method'], hint: response.hint }
}

export async function register(
  email: string,
  password: string,
  name: string,
  referralCode?: string,
  fingerprint?: string,
  next?: string,
  captchaToken?: string,
): Promise<{ ok: boolean; email: string }> {
  const utmData = getUtmData()
  const body: Record<string, string> = { email, password, name }
  if (utmData.utm_campaign) body.utm_campaign = utmData.utm_campaign
  if (utmData.utm_source) body.utm_source = utmData.utm_source
  if (utmData.utm_medium) body.utm_medium = utmData.utm_medium
  if (utmData.utm_term) body.utm_term = utmData.utm_term
  if (utmData.utm_content) body.utm_content = utmData.utm_content
  if (utmData.referrer) body.referrer = utmData.referrer
  if (utmData.landing_page) body.landing_page = utmData.landing_page
  if (referralCode) body.referral_code = referralCode
  if (fingerprint) body.fingerprint = fingerprint
  // next — куда вернуть юзера ПОСЛЕ подтверждения email. Бэк (когда будет готов)
  // сохранит его к verification_token и вошьёт в ссылку письма (?next=). Пока бэк
  // это поле игнорит (RegisterRequest без extra='forbid') — регистрацию не ломает.
  if (next && next !== '/') body.next = next
  if (captchaToken) body.captcha_token = captchaToken
  const response = await api.request<{ ok: boolean; message: string; email: string }>('/api/v1/auth/register', {
    method: 'POST',
    body,
    skipAuth: true,
  })
  return { ok: response.ok, email: response.email }
}

export async function resendVerification(email: string, captchaToken?: string): Promise<{ ok: boolean; message: string }> {
  const body: Record<string, string> = { email }
  if (captchaToken) body.captcha_token = captchaToken
  return api.request<{ ok: boolean; message: string }>('/api/v1/auth/resend-verification', {
    method: 'POST',
    body,
    skipAuth: true,
  })
}

export async function telegramLogin(authData: {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
  auth_date: number
  hash: string
}): Promise<User | RequiresTwoFactor> {
  const referralCode = localStorage.getItem('referral_code')
  const utmData = getUtmData()
  const body: Record<string, unknown> = { ...authData }
  if (utmData.utm_campaign) body.utm_campaign = utmData.utm_campaign
  if (referralCode) body.referral_code = referralCode
  if (utmData.utm_source) body.utm_source = utmData.utm_source
  if (utmData.utm_medium) body.utm_medium = utmData.utm_medium
  if (utmData.utm_term) body.utm_term = utmData.utm_term
  if (utmData.utm_content) body.utm_content = utmData.utm_content
  if (utmData.referrer) body.referrer = utmData.referrer
  if (utmData.landing_page) body.landing_page = utmData.landing_page
  const response = await api.request<LoginResponse | RequiresTwoFactor>('/api/v1/auth/telegram', {
    method: 'POST',
    body,
    skipAuth: true,
  })
  if (isRequiresTwoFactor(response)) return response
  api.setTokens(response.access_token, response.refresh_token)
  cacheAutoSound(response.user.auto_sound)
  if (referralCode) localStorage.removeItem('referral_code')
  return response.user
}

export async function getProfile(): Promise<User> {
  const res = await api.request<{ ok: boolean; user: User }>('/api/v1/user/me')
  // Единственная точка, через которую профиль приходит регулярно (поллинг
  // в AuthContext) — тут и держим зеркало настройки авто-звука актуальным.
  cacheAutoSound(res.user.auto_sound)
  return res.user
}

// Поля профиля, которые фронт может обновлять через PATCH /user/me.
// Бэк принимает: name, phone, email, default_chat_context_mode (и др.).
export interface UserProfilePatch {
  name?: string
  phone?: string
  email?: string
  default_chat_context_mode?: string
  auto_sound?: boolean
}

export async function updateUserProfile(patch: UserProfilePatch): Promise<User> {
  const res = await api.request<{ ok: boolean; user: User }>('/api/v1/user/me', {
    method: 'PATCH',
    body: patch,
  })
  cacheAutoSound(res.user.auto_sound)
  return res.user
}

// Смена почты с подтверждением (владелец 27.09): код на текущую почту → ссылка на новую.
// Почта в аккаунте меняется только по клику на ссылку; PATCH /user/me её больше не меняет.
export interface EmailChangeState {
  pending: boolean
  stage?: 'code' | 'link'
  new_email?: string
  // куда ушло письмо текущего шага: код — на текущую почту (маской), ссылка — на новую
  sent_to?: string
  // письмо не ушло (почтовый сервис не ответил) — человеку предлагаем «Отправить ещё раз»
  sent?: boolean
}

export function getEmailChange(): Promise<EmailChangeState> {
  return api.request<EmailChangeState>('/api/v1/user/me/email-change')
}

export function startEmailChange(email: string): Promise<EmailChangeState> {
  return api.request<EmailChangeState>('/api/v1/user/me/email-change', { method: 'POST', body: { email } })
}

export function submitEmailChangeCode(code: string): Promise<EmailChangeState> {
  return api.request<EmailChangeState>('/api/v1/user/me/email-change/code', { method: 'POST', body: { code } })
}

export function resendEmailChange(): Promise<EmailChangeState> {
  return api.request<EmailChangeState>('/api/v1/user/me/email-change/resend', { method: 'POST' })
}

export function cancelEmailChange(): Promise<EmailChangeState> {
  return api.request<EmailChangeState>('/api/v1/user/me/email-change', { method: 'DELETE' })
}

// user_id — чей аккаунт сменил почту: страница подтверждения обновляет сессию только ему
export function confirmEmailChange(token: string): Promise<{ ok: boolean; email: string; user_id?: string }> {
  return api.request<{ ok: boolean; email: string; user_id?: string }>('/api/v1/user/email-change/confirm', { method: 'POST', body: { token } })
}

export function logout() {
  api.setTokens(null, null)
}

export function isAuthenticated() {
  return api.isAuthenticated()
}

export async function requestPasswordReset(email: string, captchaToken?: string): Promise<void> {
  const body: Record<string, string> = { email }
  if (captchaToken) body.captcha_token = captchaToken
  await api.request('/api/v1/auth/password-reset/request', {
    method: 'POST',
    body,
    skipAuth: true,
  })
}

export async function verifyResetCode(email: string, code: string): Promise<void> {
  await api.request('/api/v1/auth/password-reset/verify', {
    method: 'POST',
    body: { email, code },
    skipAuth: true,
  })
}

export async function resetPassword(email: string, code: string, new_password: string): Promise<void> {
  await api.request('/api/v1/auth/password-reset/confirm', {
    method: 'POST',
    body: { email, code, new_password },
    skipAuth: true,
  })
}


// ── Удаление аккаунта ───────────────────────────────────────────────
// Двухшаговое: запрос кода на почту → подтверждение кодом. Бэк сжигает
// токены, отменяет подписку и закрывает повторную регистрацию
// (см. backend services/account_deletion.py).

export interface DeleteAccountRequestResponse {
  ok: boolean
  message: string
  email_masked: string
  credits_to_burn: number
}

// ── Настройки 2FA в ЛК (Bearer) ──────────────────────────────────────

export interface TwoFAStatus {
  ok: boolean
  enabled: boolean
  mandatory: boolean
  method: 'email' | 'telegram' | 'totp' | null
  email_masked: string | null
  telegram_linked: boolean
  telegram_bot_username: string | null
  totp_confirmed: boolean
  has_password: boolean
}

export async function getTwoFAStatus(): Promise<TwoFAStatus> {
  return api.request<TwoFAStatus>('/api/v1/user/2fa')
}

export async function requestSettingsChallenge(method?: 'email'): Promise<{ challenge_id: string; method: string; hint?: string | null }> {
  return api.request('/api/v1/auth/2fa/challenge', {
    method: 'POST',
    body: { purpose: 'settings', ...(method ? { method } : {}) },
  })
}

export async function enableTwoFA(params: { method: string; password?: string; challenge_id?: string; code?: string }): Promise<void> {
  await api.request('/api/v1/user/2fa/enable', { method: 'POST', body: params })
}

export async function disableTwoFA(params: { password?: string; challenge_id?: string; code?: string }): Promise<void> {
  await api.request('/api/v1/user/2fa/disable', { method: 'POST', body: params })
}

export async function totpSetup(): Promise<{ secret: string; otpauth_url: string }> {
  return api.request('/api/v1/user/2fa/totp/setup', { method: 'POST' })
}

export async function totpConfirm(code: string, identity: { password?: string; challenge_id?: string; identity_code?: string }): Promise<void> {
  await api.request('/api/v1/user/2fa/totp/confirm', { method: 'POST', body: { code, ...identity } })
}

export async function telegramTwoFATest(): Promise<void> {
  await api.request('/api/v1/user/2fa/telegram/test', { method: 'POST' })
}

export async function requestAccountDeletion(): Promise<DeleteAccountRequestResponse> {
  return api.request<DeleteAccountRequestResponse>('/api/v1/user/account/delete/request', {
    method: 'POST',
  })
}

export async function confirmAccountDeletion(
  code: string,
  reason?: string,
): Promise<{ ok: boolean; message: string; credits_burned: number }> {
  return api.request('/api/v1/user/account/delete/confirm', {
    method: 'POST',
    body: reason ? { code, reason } : { code },
  })
}
